/**
 * Lumiveta API Service
 * Handles all backend communication
 */

const API_BASE = '';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('lumiveta_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('lumiveta_token', token);
    } else {
      localStorage.removeItem('lumiveta_token');
    }
  }

  getToken() {
    // SECURITY FIX: Always read fresh from localStorage to avoid stale tokens
    // This prevents 403 errors when the cached this.token is outdated
    const freshToken = localStorage.getItem('lumiveta_token');
    console.log('[API] Token refresh - reading from localStorage:', !!freshToken);
    return freshToken;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle non-JSON responses gracefully
      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { detail: text || 'Unknown error occurred' };
      }

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth endpoints
  async register(email, password, name, preferredLanguage = 'en') {
    const data = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, preferred_language: preferredLanguage }),
    });
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async login(email, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    // Only set token if 2FA is NOT required
    if (data.token && !data.requires_2fa) {
      this.setToken(data.token);
    }
    return data;
  }

  // Admin 2FA verification
  async verify2FA(email, otpCode, tempToken) {
    const data = await this.request('/api/auth/verify-2fa', {
      method: 'POST',
      body: JSON.stringify({ email, otp_code: otpCode, temp_token: tempToken }),
    });
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  // Resend 2FA code
  async resend2FA(email, password) {
    return this.request('/api/auth/resend-2fa', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async forgotPassword(email) {
    return this.request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, origin_url: window.location.origin }),
    });
  }

  async resetPassword(token, newPassword) {
    return this.request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  async getMe() {
    // Send user's local timezone for lazy reset logic
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    return this.request('/api/auth/me', {
      headers: {
        'X-User-Timezone': timezone
      }
    });
  }

  logout() {
    this.setToken(null);
    localStorage.removeItem('lumiveta_user');
  }

  // Reflection endpoints
  async generateReflection(userInput, language = 'en') {
    // Capture user's local timezone for rolling reset logic
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    
    return this.request('/api/reflections/generate', {
      method: 'POST',
      headers: {
        'X-User-Timezone': timezone
      },
      body: JSON.stringify({ 
        user_input: userInput, 
        language
      }),
    });
  }

  async getReflectionHistory(limit = 50, offset = 0) {
    return this.request(`/api/reflections/history?limit=${limit}&offset=${offset}`);
  }

  async getReflection(reflectionId) {
    return this.request(`/api/reflections/${reflectionId}`);
  }

  async saveReflection(reflectionId, fieldNotes) {
    return this.request(`/api/reflections/${reflectionId}/save`, {
      method: 'POST',
      body: JSON.stringify({ field_notes: fieldNotes }),
    });
  }

  // Update user_input ONLY - no AI generation (for Gold Mine editing)
  async updateUserInput(reflectionId, userInput, fieldNotes = null) {
    const body = { user_input: userInput };
    if (fieldNotes && Object.keys(fieldNotes).length > 0) {
      body.field_notes = fieldNotes;
    }
    return this.request(`/api/reflections/${reflectionId}/user-input`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  // NEW: Save reflection to Gold Mine (manual save - no autosave)
  async saveToGoldMine(reflectionData, fieldNotes = null) {
    return this.request('/api/reflections/save-to-goldmine', {
      method: 'POST',
      body: JSON.stringify({ 
        reflection_data: reflectionData,
        field_notes: fieldNotes
      }),
    });
  }

  async deleteReflection(reflectionId) {
    return this.request(`/api/reflections/${reflectionId}`, {
      method: 'DELETE',
    });
  }

  // ============== SELF-HEALING BILLING BRIDGE ==============
  
  // Get billing status to determine checkout routing
  async getBillingStatus() {
    return this.request('/api/payments/billing-status');
  }
  
  // Create Customer Portal session for existing customers
  async createCustomerPortal() {
    return this.request('/api/payments/customer-portal', {
      method: 'POST',
    });
  }
  
  // Activate subscription immediately (end trial, charge card)
  async activateSubscription() {
    return this.request('/api/payments/activate-subscription', {
      method: 'POST',
    });
  }
  
  // Smart upgrade handler - routes appropriately based on user status
  // For TRIAL users: Goes to Checkout with card pre-filled + coupon code field
  // For others: Routes to portal or fresh checkout
  async smartUpgrade(locale = 'en') {
    try {
      // First check billing status (also triggers self-healing sync of Stripe IDs)
      const billingStatus = await this.getBillingStatus();
      
      // TRIAL USERS: Go to Checkout (card pre-filled, can add coupon code)
      // The checkout endpoint will use their existing stripe_customer_id
      if (billingStatus.subscription_status === 'trialing' && billingStatus.has_stripe_customer) {
        const checkoutResult = await this.createCheckout('premium', locale);
        return { 
          type: 'checkout', 
          url: checkoutResult.checkout_url,
          message: locale === 'es' ? 'Abriendo pago...' : 'Opening checkout...'
        };
      }
      
      // PREMIUM USERS: Go to Customer Portal (manage subscription)
      if (billingStatus.use_customer_portal && billingStatus.has_stripe_customer) {
        const portalResult = await this.createCustomerPortal();
        if (portalResult.portal_url && !portalResult.fallback_to_checkout) {
          return { 
            type: 'portal', 
            url: portalResult.portal_url,
            message: locale === 'es' ? 'Abriendo portal...' : 'Opening billing portal...'
          };
        }
      }
      
      // NEW USERS: Fresh checkout
      const checkoutResult = await this.createCheckout('premium', locale);
      return { 
        type: 'checkout', 
        url: checkoutResult.checkout_url,
        trial_granted: checkoutResult.trial_granted,
        message: checkoutResult.trial_granted 
          ? (locale === 'es' ? 'Iniciando prueba gratuita...' : 'Starting free trial...') 
          : (locale === 'es' ? 'Abriendo pago...' : 'Opening checkout...')
      };
    } catch (error) {
      console.error('Smart upgrade error:', error);
      throw error;
    }
  }

  // Payment endpoints
  async createCheckout(planType, locale = 'en') {
    return this.request('/api/payments/create-checkout', {
      method: 'POST',
      body: JSON.stringify({ 
        plan_type: planType,
        origin_url: window.location.origin,
        locale: locale  // Pass 'es' for Spanish Stripe checkout
      }),
    });
  }

  async getPaymentStatus(sessionId) {
    return this.request(`/api/payments/status/${sessionId}`);
  }

  // Account endpoints
  async updateProfile(name) {
    return this.request('/api/account/profile', {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async updatePassword(currentPassword, newPassword) {
    return this.request('/api/account/password', {
      method: 'PUT',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  async deleteAccount() {
    return this.request('/api/account', {
      method: 'DELETE',
    });
  }

  // Email preferences
  async getEmailPreferences() {
    return this.request('/api/users/email-preferences');
  }

  async updateEmailPreferences(enabled, language) {
    return this.request('/api/users/email-preferences', {
      method: 'PUT',
      body: JSON.stringify({ enabled, language }),
    });
  }

  // Admin endpoints
  async getAdminWhitelist() {
    return this.request('/api/admin/whitelist');
  }

  async manageAdminWhitelist(email, action) {
    return this.request('/api/admin/whitelist', {
      method: 'POST',
      body: JSON.stringify({ email, action }),
    });
  }

  async getAdminSettings() {
    return this.request('/api/admin/settings');
  }

  async updateAdminSetting(settingKey, settingValue) {
    return this.request('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify({ setting_key: settingKey, setting_value: settingValue }),
    });
  }

  async adminUserRecovery(email) {
    return this.request('/api/admin/user-recovery', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async getAdminUsers() {
    return this.request('/api/admin/users');
  }

  async masterRecovery(recoveryKey) {
    const data = await this.request('/api/admin/master-recovery', {
      method: 'POST',
      body: JSON.stringify({ recovery_key: recoveryKey }),
    });
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  // Public settings
  async getPublicSettings() {
    return this.request('/api/settings/public');
  }

  // Stripe Customer Portal
  async createPortalSession() {
    return this.request('/api/payments/create-portal-session', {
      method: 'POST',
      body: JSON.stringify({ origin_url: window.location.origin }),
    });
  }

  // Push Notifications
  async getNotificationStatus() {
    return this.request('/api/notifications/status');
  }

  async subscribeToNotifications(subscription, enabled = true) {
    return this.request('/api/notifications/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription, enabled }),
    });
  }

  async unsubscribeFromNotifications() {
    return this.request('/api/notifications/unsubscribe', {
      method: 'POST',
    });
  }

  // Health check
  async healthCheck() {
    return this.request('/api/health');
  }
}

export const api = new ApiService();
export default api;
